import Loading from '@/components/Loading/Loading';

const baseClass =
    'self-center transition-colors duration-200 ease-in-out inline-flex items-center border rounded-md shadow-sm focus:outline-none';

const btnSizeClasses = {
    xs: 'px-2 py-1 text-xs font-light',
    sm: 'px-2 py-1 text-sm font-light',
    md: 'px-4 py-2 text-base font-normal',
    lg: 'px-4 py-2 text-lg font-normal',
};

const iconSizeClasses = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
};

const variants = {
    outline: {
        primary:
            'border-primary text-primary hover:bg-primary-50 focus:ring-primary-950',
        secondary:
            'border-secondary-900 text-secondary-950 hover:bg-secondary-300 focus:ring-secondary-500',
        danger: 'border-danger-500 text-danger-500 hover:bg-danger-100 focus:ring-danger-500',
        success:
            'border-success-500 text-success-500 hover:bg-success-100 focus:ring-success-500',
        warning:
            'border-warning-500 text-warning-500 hover:bg-warning-100 focus:ring-warning-500',
        info: 'border-info-500 text-info-500 hover:bg-info-100 focus:ring-info-500',
        neutral:
            'border-neutral-300 text-neutral-700 hover:bg-neutral-100 focus:ring-neutral-500',
    },
    solid: {
        primary:
            'text-white bg-primary hover:bg-primary-950 focus:ring-primary',
        secondary:
            'text-secondary-200 bg-secondary-500 hover:bg-secondary-600 focus:ring-secondary-400',
        danger: 'text-white bg-danger-600 hover:bg-danger-700 focus:ring-danger-500',
        success:
            'text-white bg-success hover:bg-success-700 focus:ring-success',
        warning:
            'text-white bg-warning hover:bg-warning-500 focus:ring-warning',
        info: 'text-white bg-info hover:bg-info-700 focus:ring-info',
        neutral: 'text-neutral-700 bg-neutral-200 hover:bg-neutral-300',
    },
    flat: {
        primary:
            'border-transparent text-primary bg-transparent hover:bg-primary-50 focus:ring-primary-950',
        secondary:
            'border-transparent text-secondary-700 bg-transparent hover:bg-secondary-50 focus:ring-secondary-500',
        danger: 'border-transparent text-danger-700 bg-transparent hover:bg-danger-50 focus:ring-danger-500',
        success:
            'border-transparent text-success-700 bg-transparent hover:bg-success-50 focus:ring-success-500',
        warning:
            'border-transparent text-warning-700 bg-transparent hover:bg-warning-50 focus:ring-warning-500',
        info: 'border-transparent text-info-700 bg-transparent hover:bg-info-50 focus:ring-info-500',
        neutral:
            'border-transparent text-neutral-700 bg-transparent hover:bg-neutral-50 focus:ring-neutral-500',
    },
};

function getButtonClasses({
    size,
    variant,
    appearance,
    disabled,
    loading,
    className,
}) {
    return [
        baseClass,
        btnSizeClasses[size],
        variants[appearance][variant],
        (disabled || loading) && 'opacity-50 cursor-not-allowed',
        className,
    ]
        .filter(Boolean)
        .join(' ');
}

const Button = ({
    onClick,
    children,
    type = 'button',
    className = '',
    disabled = false,
    variant = 'primary', // 'primary' or 'secondary' or 'danger' or 'success' or 'warning' or 'info' or 'neutral'
    icon: Icon,
    iconPosition = 'left',
    iconSize,
    loading,
    size = 'md',
    appearance = 'solid', // 'solid' or 'outline' or 'flat'
    ...rest
}) => {
    function renderLeftIcon() {
        if (Icon && iconPosition === 'left') {
            return loading ? (
                <Loading className="me-2" size={size} variant={variant} />
            ) : (
                <Icon
                    size={iconSize || iconSizeClasses[size]}
                    className={children ? 'me-2' : ''}
                />
            );
        }

        return null;
    }

    function renderRightIcon() {
        if (Icon && iconPosition === 'right') {
            return loading ? (
                <Loading className="ms-2" size={size} variant={variant} />
            ) : (
                <Icon className="ms-2" />
            );
        }

        return null;
    }

    return (
        <button
            type={type}
            onClick={loading ? null : onClick}
            disabled={disabled}
            className={getButtonClasses({
                size,
                variant,
                appearance,
                disabled,
                loading,
                className,
            })}
            {...rest}
        >
            {renderLeftIcon()}
            {loading && !Icon && (
                <Loading className="me-2" size={size} variant={variant} />
            )}
            {children}
            {renderRightIcon()}
        </button>
    );
};

export default Button;
